// Training's words and numbers, in one place so every screen says them the
// same way: the plan line ("3 × 4–6 @ 2 in reserve · 135 lb"), set rows,
// template / muscle / region labels, and dates in the settings' time zone.

// backend/training/models.py Session.Template (the labels the API documents).
export const TEMPLATE_LABELS = {
  full: "Full body",
  upper: "Upper",
  lower: "Lower",
  push: "Push",
  pull: "Pull",
  arms_delts: "Arms + delts",
  minimum: "Minimum dose",
  custom: "Custom",
};

// backend/training/muscles.py MUSCLES: (code, label, soreness region), in
// the order the UI lists them.
export const MUSCLES = [
  { code: "chest", label: "Chest", region: "chest" },
  { code: "lats", label: "Lats", region: "back" },
  { code: "upper_back", label: "Upper back", region: "back" },
  { code: "front_delts", label: "Front delts", region: "shoulders" },
  { code: "side_delts", label: "Side delts", region: "shoulders" },
  { code: "rear_delts", label: "Rear delts", region: "shoulders" },
  { code: "rotator_cuff", label: "Rotator cuff & scapular stabilisers", region: "shoulders" },
  { code: "traps", label: "Traps", region: "back" },
  { code: "biceps", label: "Biceps", region: "arms" },
  { code: "triceps", label: "Triceps", region: "arms" },
  { code: "forearms", label: "Forearms", region: "arms" },
  { code: "quads", label: "Quads", region: "legs" },
  { code: "hamstrings", label: "Hamstrings", region: "legs" },
  { code: "glutes", label: "Glutes", region: "hips" },
  { code: "adductors", label: "Adductors", region: "hips" },
  { code: "calves", label: "Calves", region: "legs" },
  { code: "abs", label: "Abs", region: "core" },
  { code: "obliques", label: "Obliques", region: "core" },
  { code: "low_back", label: "Low back", region: "back" },
];

// backend/training/muscles.py REGIONS — what the check-in asks soreness by.
export const REGIONS = [
  { code: "chest", label: "Chest" },
  { code: "back", label: "Back" },
  { code: "shoulders", label: "Shoulders" },
  { code: "arms", label: "Arms" },
  { code: "legs", label: "Legs" },
  { code: "hips", label: "Hips" },
  { code: "core", label: "Core" },
];

const MUSCLE_BY_CODE = Object.fromEntries(MUSCLES.map((m) => [m.code, m]));
const REGION_BY_CODE = Object.fromEntries(REGIONS.map((r) => [r.code, r]));

// An unknown code (a newer backend) still reads as words, never a crash.
const humanize = (code) => {
  const text = String(code ?? "").replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const templateLabel = (template) => TEMPLATE_LABELS[template] ?? humanize(template);
export const muscleLabel = (code) => MUSCLE_BY_CODE[code]?.label ?? humanize(code);
export const regionLabel = (code) => REGION_BY_CODE[code]?.label ?? humanize(code);
export const muscleRegion = (code) => MUSCLE_BY_CODE[code]?.region ?? null;

/** 135 → "135", 62.5 → "62.5", 22.6796 → "22.68". */
export function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 100) / 100);
}

/** "135 lb" (units default to lb). */
export function formatLoad(value, units = "lb") {
  return `${formatNumber(value)} ${units}`;
}

/** Reps in reserve as said aloud: 5 means "5 or more". */
export function formatRir(rir) {
  if (rir === null || rir === undefined) return null;
  return Number(rir) >= 5 ? "5+" : String(rir);
}

/** "4–6" (en dash), or "8" when the range is one number. */
export function repRange(low, high) {
  return low === high || high === undefined || high === null ? `${low}` : `${low}–${high}`;
}

const hasBand = (band) => typeof band === "string" && band.trim() !== "";

/**
 * The load part of a plan line or a set row, by the exercise's load kind:
 * "135 lb" (external; "40 lb each" per dumbbell), "bodyweight" /
 * "bodyweight + 10 lb" / "bodyweight, light band", "light band".
 */
function loadText(load, band, units, exercise) {
  const kind = exercise?.load_kind ?? (hasBand(band) ? "band" : "external");
  if (kind === "band") return hasBand(band) ? `${band} band` : "band";
  if (kind === "bodyweight") {
    if (hasBand(band)) return `bodyweight, ${band} band`;
    return Number(load) > 0 ? `bodyweight + ${formatLoad(load, units)}` : "bodyweight";
  }
  const each = exercise?.per_dumbbell ? " each" : "";
  return `${formatLoad(load, units)}${each}`;
}

// D9 with no number yet, in the engine's words (engine/loads.py _find_weight).
const FIND = { band: "Find your band", bodyweight: "Find your setup" };
const FIND_GUIDANCE = {
  band: (reps, rir) => `Find your band: pick one you could do about ${reps} times with ${rir} in reserve; the next set builds from it.`,
  bodyweight: (reps, rir) =>
    `Find your setup: bodyweight, a band or added weight you could do about ${reps} times with ${rir} in reserve; the next set builds from it.`,
  external: (reps, rir) => `Find your weight: pick one you could lift about ${reps} times with ${rir} in reserve; the next set builds from it.`,
};

/** D9's "what to pick" for a load kind (band, bodyweight, external), in the engine's words. */
export const findGuidance = (kind, reps, rir) => (FIND_GUIDANCE[kind] ?? FIND_GUIDANCE.external)(reps, rir);

/**
 * One planned exercise in a line: "3 × 4–6 @ 2 in reserve · 135 lb". With
 * no number yet (D9) the load reads "Find your weight" ("Find your band",
 * "Find your setup" for band and bodyweight moves). Pass the exercise
 * (from the catalog) for bodyweight, band and per-dumbbell wording.
 */
export function planLine(item, units = "lb", exercise) {
  const sets = `${item.sets} × ${repRange(item.reps_low, item.reps_high)}`;
  const rir = formatRir(item.rir);
  const head = rir === null ? sets : `${sets} @ ${rir} in reserve`;
  const kind = exercise?.load_kind;
  const finding =
    item.find_weight === true ||
    ((item.load === null || item.load === undefined) && kind !== "band" && !hasBand(item.band));
  const load = finding
    ? (FIND[kind] ?? "Find your weight")
    : loadText(item.load ?? 0, item.band, units, exercise);
  return `${head} · ${load}`;
}

/**
 * An item's reason as said under its name (W8). The engine leads with the
 * exercise ("Back squat — Find your weight: …", "Bench press 140 lb (+5):
 * …"), which the card's title already says: that lead is dropped, and what
 * is left starts its own sentence. Any other reason is as it was.
 */
export function itemReason(reason, name) {
  const text = String(reason ?? "").trim();
  if (!name) return text;
  for (const lead of [`${name} — `, `${name} `]) {
    const rest = text.startsWith(lead) ? text.slice(lead.length).trim() : "";
    if (rest) return rest.charAt(0).toUpperCase() + rest.slice(1);
  }
  return text;
}

// The reason for each number, in the order the plan line says them.
const WHY_PARTS = [
  ["sets", "Sets"],
  ["reps", "Reps"],
  ["rir", "In reserve"],
  ["load", "Weight"],
];
// A Weight line shorter than this is left out only when it is the whole
// reason ("Bodyweight." inside a longer one would be a coincidence).
const SAYS_IT_MIN = 20;
const sentence = (text) => String(text ?? "").trim().replace(/\.$/, "");

/**
 * "Why these numbers" as {key, label, text} lines — without the Weight
 * line when `shown` (the reason on the card) already says it word for word
 * (W8): the engine's reason is its load's reason, plus any easing.
 */
export function whyLines(item, shown) {
  const said = sentence(shown);
  const repeats = (text) => {
    const line = sentence(text);
    return line === said || (line.length >= SAYS_IT_MIN && said.includes(line));
  };
  return WHY_PARTS.filter(([key]) => item?.why?.[key])
    .filter(([key]) => !(key === "load" && repeats(item.why.load)))
    .map(([key, label]) => ({ key, label, text: item.why[key] }));
}

/**
 * One logged set: "135 lb × 6 @ 2 in reserve", "bodyweight + 10 lb × 8",
 * "light band × 12"; left/right reps when they differ: "× 8 (L 8 · R 7)".
 */
export function setLine(set, units = "lb", exercise) {
  const sides =
    set.reps_left !== null &&
    set.reps_left !== undefined &&
    set.reps_right !== null &&
    set.reps_right !== undefined
      ? ` (L ${set.reps_left} · R ${set.reps_right})`
      : "";
  const rir = formatRir(set.rir);
  const reserve = rir === null ? "" : ` @ ${rir} in reserve`;
  return `${loadText(set.load, set.band, units, exercise)} × ${set.reps}${sides}${reserve}`;
}

// --- dates, in the settings' time zone ---------------------------------------

/** The zone if Intl knows it, else undefined (the browser's own). */
function knownZone(timeZone) {
  if (!timeZone) return undefined;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return undefined; // an old bad value: show the browser's time instead
  }
}

const valid = (iso) => {
  if (!iso) return null;
  const date = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** The calendar day "YYYY-MM-DD" in `timeZone` — the API's from/to format. */
export function dayKey(iso, timeZone) {
  const date = valid(iso);
  if (!date) return "";
  return date.toLocaleDateString("en-CA", { timeZone: knownZone(timeZone) });
}

/**
 * "Wed, Oct 7" in `timeZone`, with the year when it isn't this year there
 * ("Tue, Dec 30, 2025").
 */
export function formatDay(iso, timeZone, now = new Date()) {
  const date = valid(iso);
  if (!date) return "";
  const zone = knownZone(timeZone);
  const year = (d) => d.toLocaleDateString("en-US", { timeZone: zone, year: "numeric" });
  return date.toLocaleDateString(undefined, {
    timeZone: zone,
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(year(date) !== year(now) ? { year: "numeric" } : {}),
  });
}

/** "6:42 PM" in `timeZone`. */
export function formatTime(iso, timeZone) {
  const date = valid(iso);
  if (!date) return "";
  return date.toLocaleTimeString(undefined, {
    timeZone: knownZone(timeZone),
    hour: "numeric",
    minute: "2-digit",
  });
}
